import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import Spinner from '../components/Spinner';
import { FiDollarSign, FiX, FiEye, FiCalendar, FiClock } from 'react-icons/fi'; // Import de FiDollarSign, FiX et FiEye

const SupplierDebtPaymentModal = ({ isOpen, onClose, onSave, debt }) => {
  const [amount, setAmount] = useState('');

  if (!isOpen) return null;

  const handleSave = () => {
    const paymentAmount = parseFloat(amount);
    if (!isNaN(paymentAmount) && paymentAmount > 0) {
      onSave(paymentAmount);
      setAmount('');
    } else {
      toast.error("Veuillez entrer un montant valide.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
        <h3 className="text-xl font-semibold mb-4">Enregistrer un paiement pour {debt?.supplierId?.name || 'fournisseur'}</h3>
        <p className="mb-2">Montant dû: {(debt?.amountRemaining || 0).toLocaleString('fr-FR')} CFA</p>
        <input type="number" placeholder="Montant payé" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full p-2 border rounded mb-4" />
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Annuler</button>
          <button onClick={handleSave} className="bg-brand-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg">Enregistrer</button>
        </div>
      </div>
    </div>
  );
};

const SupplierDebtHistoryModal = ({ isOpen, onClose, debtId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && debtId) {
      const fetchHistory = async () => {
        setLoading(true);
        try {
          // Endpoint à créer côté backend pour récupérer l'historique des paiements fournisseurs
          const response = await api.get(`/supplier-debts/${debtId}/history`);
          setHistory(response.data);
        } catch (error) {
          toast.error("Impossible de charger l'historique des paiements.");
        }
        setLoading(false);
      };
      fetchHistory();
    }
  }, [isOpen, debtId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Historique des paiements</h3>
          <button onClick={onClose}><FiX size={24} /></button>
        </div>
        {loading ? <Spinner message="Chargement de l'historique..." /> : (
          <ul className="space-y-2 max-h-80 overflow-y-auto">
            {history.length > 0 ? history.map(payment => (
              <li key={payment._id} className="flex justify-between p-2 bg-gray-100 rounded">
                <span>{new Date(payment.date).toLocaleDateString('fr-FR')}</span>
                <span className="font-bold text-green-600">{payment.amount.toLocaleString('fr-FR')} CFA</span>
              </li>
            )) : <p className="text-center text-gray-500 py-4">Aucun paiement enregistré pour cette dette.</p>}
          </ul>
        )}
      </div>
    </div>
  );
};

const SupplierDebts = () => {
  const [supplierDebts, setSupplierDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false); // Nouvel état
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false); // Nouvel état pour l'historique
  const [selectedSupplierDebt, setSelectedSupplierDebt] = useState(null); // Nouvel état

  const handleOpenPaymentModal = (debt) => {
    setSelectedSupplierDebt(debt);
    setIsPaymentModalOpen(true);
  };

  const handleOpenHistoryModal = (debt) => {
    setSelectedSupplierDebt(debt);
    setIsHistoryModalOpen(true);
  };

  const handleSavePayment = async (paymentAmount) => {
    try {
      // Endpoint à créer côté backend pour enregistrer le paiement fournisseur
      await api.post(`/supplier-debts/${selectedSupplierDebt._id}/payments`, { amount: paymentAmount });
      toast.success("Paiement fournisseur enregistré avec succès !");
      fetchSupplierDebts(); // Recharger la liste des dettes
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement du paiement fournisseur.");
    }
    setIsPaymentModalOpen(false);
  };

  const fetchSupplierDebts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/supplier-debts');
      setSupplierDebts(response.data);
    } catch (error) {
      toast.error("Impossible de charger les dettes fournisseurs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupplierDebts();
  }, []);

  const getStatusClass = (status) => {
    switch (status) {
      case 'Payé': return 'bg-green-200 text-green-900';
      case 'Partiellement payé': return 'bg-yellow-200 text-yellow-900';
      case 'Non payé': return 'bg-red-200 text-red-900';
      default: return 'bg-gray-200 text-gray-900';
    }
  };

  if (loading) {
    return <Spinner message="Chargement des dettes fournisseurs..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Dettes Fournisseurs</h2>
        {/* Un bouton pour enregistrer un paiement sur une dette fournisseur pourrait être ajouté ici */}
      </div>

      {/* Vue Mobile (Cartes) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:hidden">
        {supplierDebts.map((debt) => (
          <div key={debt._id} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between hover:shadow-md transition-all duration-200">
            <div>
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold text-gray-800">{debt.supplierId?.name || 'Fournisseur supprimé'}</h3>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${getStatusClass(debt.status)}`}>
                  {debt.status}
                </span>
              </div>
              <div className="mb-2 text-xs text-gray-500 flex justify-between bg-gray-50 p-2 rounded">
                 <span>Total: {(debt.totalAmount || 0).toLocaleString('fr-FR')} F</span>
                 <span>Payé: {((debt.totalAmount || 0) - debt.amountRemaining).toLocaleString('fr-FR')} F</span>
              </div>
              <div className="mb-3">
                <p className="text-sm text-gray-600">Montant restant</p>
                <p className="text-xl font-bold text-red-600">{debt.amountRemaining.toLocaleString('fr-FR')} CFA</p>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Date: {debt.purchaseDate ? new Date(debt.purchaseDate).toLocaleDateString('fr-FR') : '-'}
              </p>
              {debt.dueDate && (
                  <p className={`text-xs font-semibold flex items-center ${new Date(debt.dueDate) < new Date() && debt.status !== 'Payé' ? 'text-red-600' : 'text-gray-500'}`}>
                      <FiCalendar className="mr-1"/> Échéance: {new Date(debt.dueDate).toLocaleDateString('fr-FR')}
                      {new Date(debt.dueDate) < new Date() && debt.status !== 'Payé' && <span className="ml-2 bg-red-100 text-red-800 px-1 rounded text-[10px]">Retard</span>}
                  </p>
              )}
            </div>
            <div className="flex justify-end space-x-3 border-t pt-3 mt-3">
              <button onClick={(e) => { e.stopPropagation(); handleOpenPaymentModal(debt); }} className="text-gray-500 hover:text-brand-green p-2 bg-gray-100 rounded-full" title="Enregistrer un paiement">
                <FiDollarSign className="w-4 h-4" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); handleOpenHistoryModal(debt); }} className="text-gray-500 hover:text-brand-blue p-2 bg-gray-100 rounded-full" title="Voir l'historique">
                <FiEye className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white shadow-sm rounded-2xl border border-gray-100 overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full leading-normal">
            <thead className="sticky top-0 z-10">
              <tr className="border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <th className="px-5 py-3">Fournisseur</th>
                <th className="px-5 py-3">Total</th>
                <th className="px-5 py-3">Payé</th>
                <th className="px-5 py-3">Reste</th>
                <th className="px-5 py-3">Date de l'achat</th>
                <th className="px-5 py-3">Échéance</th>
                <th className="px-5 py-3">Statut</th>
                <th className="px-5 py-3">Actions</th> {/* Nouvelle colonne */}
              </tr>
            </thead>
            <tbody>
              {supplierDebts.length > 0 ? supplierDebts.map((debt) => (
                <tr key={debt._id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="px-5 py-4 text-sm">
                    <p className="text-gray-900 whitespace-no-wrap font-semibold">{debt.supplierId?.name || 'Fournisseur supprimé'}</p>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <p className="text-gray-900 whitespace-no-wrap">{(debt.totalAmount || 0).toLocaleString('fr-FR')} CFA</p>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <p className="text-green-600 whitespace-no-wrap">{((debt.totalAmount || 0) - debt.amountRemaining).toLocaleString('fr-FR')} CFA</p>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <p className="text-red-600 whitespace-no-wrap font-bold">{debt.amountRemaining.toLocaleString('fr-FR')} CFA</p>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <p className="text-gray-900 whitespace-no-wrap">
                      {debt.purchaseDate ? new Date(debt.purchaseDate).toLocaleDateString('fr-FR') : '-'}
                    </p>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    {debt.dueDate ? (
                        <div className={`flex items-center ${new Date(debt.dueDate) < new Date() && debt.status !== 'Payé' ? 'text-red-600 font-bold' : 'text-gray-600'}`}>
                            {new Date(debt.dueDate) < new Date() && debt.status !== 'Payé' && <FiClock className="mr-2"/>}
                            {new Date(debt.dueDate).toLocaleDateString('fr-FR')}
                        </div>
                    ) : <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <span className={`relative inline-block px-3 py-1 font-semibold leading-tight ${getStatusClass(debt.status)} rounded-full`}>
                      {debt.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <div className="flex items-center space-x-3">
                      <button onClick={(e) => { e.stopPropagation(); handleOpenPaymentModal(debt); }} className="text-gray-500 hover:text-brand-green" title="Enregistrer un paiement">
                        <FiDollarSign className="w-5 h-5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleOpenHistoryModal(debt); }} className="text-gray-500 hover:text-brand-blue" title="Voir l'historique des paiements">
                        <FiEye className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="8" className="text-center py-10 text-gray-500">Aucune dette fournisseur enregistrée.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <SupplierDebtPaymentModal isOpen={isPaymentModalOpen} onClose={() => setIsPaymentModalOpen(false)} onSave={handleSavePayment} debt={selectedSupplierDebt} />
      <SupplierDebtHistoryModal isOpen={isHistoryModalOpen} onClose={() => setIsHistoryModalOpen(false)} debtId={selectedSupplierDebt?._id} />
    </div>
  );
};

export default SupplierDebts;